import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArticleCrm, ArticleCrmAutomation } from 'src/app/classes/article-crm.class';
import { ArticleDayreport } from 'src/app/classes/article-dayreport.class';
import { Article } from 'src/app/classes/article.class';
import { Customer } from 'src/app/classes/customer.class';
import { Project } from 'src/app/classes/project.class';
import { TriggerFieldOption } from '../pages/article-management/article-crm-trigger-fields.util';
import { ApiService } from './api/api.service';

export interface AfasSyncResult {
    synced: number;
    failed: { id: number; crm_article_id: string; error: string }[];
}

// One row per customer, or per project when that customer has project-specific bestekposten.
export interface UnprocessedOverviewGroup {
    customer: Customer;
    project: Project | null;
    dayreport_count: number;
    first_date: Date;
    last_date: Date;
}

export interface LinkArticleCrmResult {
    articleCrm: ArticleCrm;
    // Other bestekposten the backend unlinked to make room for this combo (see
    // ArticlesController::linkArticleCrm) - apply via applyLinkResult() so they don't keep
    // showing as linked in a caller's local state until a full reload.
    clearedArticleCrms: ArticleCrm[];
}

@Injectable({ providedIn: 'root' })
export class ArticlesService {
    constructor(private apiService: ApiService) {}

    public getList(): Observable<Article[]> {
        return this.apiService.getCallData$<Article[]>('articles');
    }

    public getByCustomer(customerId: number): Observable<ArticleCrm[]> {
        return this.apiService.getCallData$<ArticleCrm[]>(`articles/customer/${customerId}`);
    }

    public getProjectsByCustomer(customerId: number): Observable<Project[]> {
        return this.apiService.getCallData$<Project[]>(`articles/customer/${customerId}/projects`);
    }

    public updateCrmArticle(
        articleCrmId: number,
        changes: Partial<
            Pick<ArticleCrm, 'afas_project_id' | 'description' | 'unit' | 'required_quantity' | 'price_per_unit'>
        >
    ): Observable<ArticleCrm> {
        return this.apiService
            .patchCall$<ArticleCrm>(`articles/crm/${articleCrmId}`, changes)
            .pipe(map(response => response.data));
    }

    public updateCrmArticleAutomations(
        articleCrmId: number,
        automations: ArticleCrmAutomation[]
    ): Observable<ArticleCrm> {
        return this.apiService
            .patchCall$<ArticleCrm>(`articles/crm/${articleCrmId}/automations`, { automations })
            .pipe(map(response => response.data));
    }

    public importForCustomer(
        customerId: number,
        rows: Partial<ArticleCrm>[],
        afasProjectId: string | null = null
    ): Observable<ArticleCrm[]> {
        return this.apiService
            .postCall$<ArticleCrm[]>(`articles/customer/${customerId}/import`, {
                rows,
                afas_project_id: afasProjectId,
            })
            .pipe(map(response => response.data));
    }

    // Used by the "link bestekpost" dialog: adds the article/material combo to the bestekpost as an
    // automation without source, so one bestekpost can serve multiple combinations.
    public linkCrmArticle(
        articleCrmId: number,
        changes: Pick<ArticleCrmAutomation, 'article_id' | 'material_id'>
    ): Observable<LinkArticleCrmResult> {
        return this.apiService
            .postCall$<LinkArticleCrmResult>(`articles/crm/${articleCrmId}/link`, changes)
            .pipe(map(response => response.data));
    }

    // Merges a linkCrmArticle() result into a local crmArticles list: replaces the linked row and
    // any other row the backend unlinked to make room for it.
    public applyLinkResult(crmArticles: ArticleCrm[], result: LinkArticleCrmResult): ArticleCrm[] {
        const updatedById = new Map(
            [result.articleCrm, ...result.clearedArticleCrms].map(crmArticle => [crmArticle.id, crmArticle])
        );

        return crmArticles.map(crmArticle => updatedById.get(crmArticle.id) ?? crmArticle);
    }

    // Start de AI op de achtergrond; geeft de bestekposten terug die nu op 'pending' staan.
    public startAiSuggestionsForCustomer(
        customerId: number,
        afasProjectId: string | null,
        triggerFields: Record<'planning' | 'dayreport', TriggerFieldOption[]>
    ): Observable<ArticleCrm[]> {
        return this.apiService
            .postCall$<ArticleCrm[]>(`articles/customer/${customerId}/ai-suggestions`, {
                afas_project_id: afasProjectId,
                trigger_fields: triggerFields,
            })
            .pipe(map(response => response.data));
    }

    // Start de AI opnieuw voor één bestekpost; geeft de bestekpost terug die nu op 'pending' staat.
    public startAiSuggestion(
        articleCrmId: number,
        triggerFields: Record<'planning' | 'dayreport', TriggerFieldOption[]>
    ): Observable<ArticleCrm> {
        return this.apiService
            .postCall$<ArticleCrm>(`articles/crm/ai-suggestion/${articleCrmId}`, { trigger_fields: triggerFields })
            .pipe(map(response => response.data));
    }

    public deleteArticleCrm(articleCrmId: number): Observable<number> {
        return this.apiService.deleteCall$<number>(`articles/crm/${articleCrmId}`).pipe(map(response => response.data));
    }

    public deleteUnlinkedForCustomer(customerId: number, afasProjectId: string | null): Observable<number[]> {
        return this.apiService
            .deleteCall$<number[]>(
                `articles/customer/${customerId}/unlinked`,
                afasProjectId ? { afas_project_id: afasProjectId } : {}
            )
            .pipe(map(response => response.data));
    }

    public deleteAllForCustomer(customerId: number, afasProjectId: string | null): Observable<number[]> {
        return this.apiService
            .deleteCall$<number[]>(
                `articles/customer/${customerId}`,
                afasProjectId ? { afas_project_id: afasProjectId } : {}
            )
            .pipe(map(response => response.data));
    }

    public syncToAfas(articleCrmId: number): Observable<void> {
        return this.apiService.postCall$<void>(`articles/crm/${articleCrmId}/sync-afas`).pipe(map(() => undefined));
    }

    public syncCustomerToAfas(customerId: number): Observable<AfasSyncResult> {
        return this.apiService
            .postCall$<AfasSyncResult>(`articles/customer/${customerId}/sync-afas`)
            .pipe(map(response => response.data));
    }

    public getProductionByCustomer(customerId: number): Observable<ArticleDayreport[]> {
        return this.apiService.getCallData$<ArticleDayreport[]>(`articles/dayreports/customer/${customerId}`);
    }

    public getUnprocessedOverview(statusKey: string | null = null): Observable<UnprocessedOverviewGroup[]> {
        return this.apiService.getCallData$<UnprocessedOverviewGroup[]>(
            'articles/dayreports/unprocessed-overview',
            statusKey ? { status_key: statusKey } : {}
        );
    }
}
